// Einstellungen — effective system configuration, the tenant email signature and, since #1431,
// everything the header's "More" menu held that belongs to a business. The
// FOLLOWUP_SEND_ENABLED flag remains an env kill switch; the
// runtime master send toggle below it is owner-only (#262: it gates EVERY tenant's sending),
// so tenant admins see a read-only status pill instead of the switch.
import React from "react";
import { Card, Button, FormField, StatusPill, Banner, Switch } from "@/ds";
import type { RichTextEditorValue } from "./workspace-review/rich-text-editor";
import { SignatureEditor, type SignatureEditorLogo } from "./signature-editor";
import signatureStyles from "./signature-editor.module.css";
import { PanelLabel } from "@/lib";
import { useRoutedState } from "@/lib/routed-state";
import type { SettingsSection } from "../router";
import { cockpit, withTenantContext } from "@/api";
import { useLiveInvalidation } from "@/live/live-provider";
import type { AppConfigInfo } from "@/api";
import { useT } from "../i18n";
import { BusinessPolicyStatus } from "./business-policy-status";
import { BusinessLogoCard } from "./settings-logo";
import type { PushToast } from "@/notifications/toast-queue";
import "../styles/settings-system.css";

export interface SettingsScreenProps {
  config: AppConfigInfo | null;
  pushToast: PushToast;
  /** `background` re-reads without blanking the configuration the shell renders if it fails. */
  onConfigChanged: (options?: { background?: boolean }) => Promise<void>;
  /** Owner-only master send toggle (#262); false renders the read-only pill instead. */
  canToggleMasterSend: boolean;
  /** #1228: which section the URL asks for. Absent means the screen keeps it in its own state. */
  section?: SettingsSection;
  onSectionChange?: (section: SettingsSection) => void;
  /**
   * #1431: false for an owner who has not entered a business. The business group is not offered —
   * its reads would be refused without a tenant — and a line says how to choose one instead.
   */
  inBusiness?: boolean;
  /**
   * #1431: sections whose screens App owns. Each one is offered exactly when its content is given,
   * which is how the service mailboxes appear only for an owner. Offering is presentation; every
   * route behind them re-checks the caller server-side.
   */
  setup?: React.ReactNode;
  /** #1432: every way enquiries arrive, and where replies leave from. Needs the router and session. */
  email?: React.ReactNode;
  ruleValues?: React.ReactNode;
  serviceMailboxes?: React.ReactNode;
  onOpenBusinesses?: () => void;
}

const FLAGS: Array<{ key: keyof AppConfigInfo["flags"]; failClosed?: boolean }> = [
  { key: "ai_enabled" },
  { key: "followup_send_enabled", failClosed: true },
  { key: "bp_export_enabled", failClosed: true },
  { key: "external_lookup_enabled" },
];

export function SettingsScreen({
  config,
  pushToast,
  onConfigChanged,
  canToggleMasterSend,
  section: routedSection,
  onSectionChange,
  inBusiness = true,
  setup,
  email,
  ruleValues,
  serviceMailboxes,
  onOpenBusinesses,
}: SettingsScreenProps) {
  const t = useT();
  // The sidebar owns navigation; this list still resolves allowed sections and legacy URLs.
  const sections: Array<{ id: SettingsSection; label: string }> = [
    ...(inBusiness ? [
      ...(setup ? [{ id: "setup" as const, label: t("shell.gettingStarted") }] : []),
      { id: "email" as const, label: t("settings.section.email") },
      { id: "signature" as const, label: t("settings.section.signature") },
      { id: "logo" as const, label: t("settings.section.logo") },
      ...(ruleValues ? [{ id: "rule-values" as const, label: t("settings.section.ruleValues") }] : []),
      { id: "system" as const, label: t("settings.section.system") },
    ] : []),
    ...(serviceMailboxes
      ? [{ id: "service-mailboxes" as const, label: t("shell.platformMailboxes") }]
      : []),
  ];
  // A bare /settings keeps meaning System status, so the links that always went there — the
  // Overview footer, "Enable in settings" — still land where they did.
  const fallback: SettingsSection = inBusiness ? "system" : sections[0]?.id ?? "system";
  const [chosen] = useRoutedState<SettingsSection>(routedSection, onSectionChange, fallback);
  // A section this session is not offered shows the default instead of an empty page. The address
  // is left as it is: a section can arrive with the configuration, and rewriting before that would
  // throw a good link away.
  const section = sections.some((s) => s.id === chosen) ? chosen : fallback;
  const sectionLabel = sections.find((s) => s.id === section)?.label ?? "";
  return (
    <div className="page page--management page--settings">
      <div className="page-head">
        <div>
          <h1>{sectionLabel || t("settings.title")}</h1>
          {!inBusiness && section !== "service-mailboxes" && (
            <p className="management-copy" data-testid="settings-no-business">
              {t("settings.noBusiness")}{" "}
              {onOpenBusinesses && (
                <button type="button" className="linklike" onClick={onOpenBusinesses}>
                  {t("shell.noBusinessCta")}
                </button>
              )}
            </p>
          )}
        </div>
      </div>
      {sections.length > 0 && (
        <div className="settings-content seclayout__content">
          {section === "setup" && setup}
          {/* #1432: every way enquiries arrive, including the address System used to show. */}
          {section === "email" && email}
          {section === "rule-values" && ruleValues}
          {section === "signature" && <SignatureCard pushToast={pushToast} />}
          {section === "logo" && (
            <BusinessLogoCard logo={config?.business_logo ?? null} onChanged={onConfigChanged} pushToast={pushToast} />
          )}
          {section === "system" && (
            <SystemCard config={config} pushToast={pushToast} onConfigChanged={onConfigChanged} canToggleMasterSend={canToggleMasterSend} />
          )}
          {section === "service-mailboxes" && serviceMailboxes}
        </div>
      )}
    </div>
  );
}

/** #278: the tenant's customer email signature (legal Impressum block appended to every
 *  generated draft). Empty = no block (default business falls back to the built-in one). */
function SignatureCard({ pushToast }: { pushToast: SettingsScreenProps["pushToast"] }) {
  const t = useT();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [source, setSource] = React.useState<"tenant" | "default" | "none">("none");
  const [textDe, setTextDe] = React.useState("");
  const [htmlDe, setHtmlDe] = React.useState("");
  const [logo, setLogo] = React.useState<SignatureEditorLogo | null>(null);
  const [logoBusy, setLogoBusy] = React.useState(false);
  const [contentValid, setContentValid] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  /**
   * Bumped whenever the stored value is adopted from the server. `LexicalComposer` reads its
   * initial content once and ignores later prop changes, so a refresh that replaces the content
   * has to remount the editor — otherwise the screen shows one thing and would save another.
   */
  const [revision, setRevision] = React.useState(0);

  // True once the operator has typed: a background refresh then leaves the editor alone.
  const edited = React.useRef(false);
  const storedState = React.useRef("");
  const [changedElsewhere, setChangedElsewhere] = React.useState(false);

  // Deps deliberately empty: depending on `t` would re-run the effect every render when a
  // translator identity is unstable (as in the test i18n mock) — an infinite load loop.
  // Live refetches can overlap; only the newest response may paint.
  const sequence = React.useRef(0);
  const load = React.useCallback((opts?: { background?: boolean }) => {
    const background = opts?.background === true;
    if (!background) setLoading(true);
    setError(null);
    const mine = ++sequence.current;
    cockpit
      .getEmailSignature()
      .then((state) => {
        if (mine !== sequence.current) return;
        setSource(state.source);
        // Never overwrite what the operator is typing; tell them instead that the stored value
        // moved, so saving stays their decision.
        if (background && edited.current) {
          if (JSON.stringify(state) !== storedState.current) setChangedElsewhere(true);
          return;
        }
        storedState.current = JSON.stringify(state);
        setTextDe(state.value?.text_de ?? "");
        setHtmlDe(state.value?.html_de ?? "");
        setLogo(state.logo ? { ...state.logo, preview_url: withTenantContext(state.logo.preview_url) } : null);
        setRevision((n) => n + 1);
        edited.current = false;
        setChangedElsewhere(false);
      })
      .catch((e) => { if (mine === sequence.current && !background) setError(e?.message || ""); })
      .finally(() => { if (mine === sequence.current && !background) setLoading(false); });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // Another admin's change to the same settings must not sit unseen on this screen (#544). It is
  // a background refresh: no spinner, and no surprise edit of the open editor.
  const invalidations = useLiveInvalidation("settings");
  React.useEffect(() => {
    if (invalidations === 0) return;
    load({ background: true });
  }, [invalidations, load]);

  /**
   * Only an actual edit replaces what will be saved. Merely opening the page publishes a snapshot
   * of the parsed content, and adopting that would rewrite a hand-written signature into the
   * editor's own vocabulary without anybody asking for it.
   */
  const onEditorChange = React.useCallback((value: RichTextEditorValue) => {
    if (!value.changed) return;
    edited.current = true;
    setTextDe(value.text);
    setHtmlDe(value.html);
  }, []);

  const save = () => {
    setBusy(true);
    cockpit
      .setEmailSignature({ text_de: textDe, html_de: htmlDe, ...(logo ? { logo_id: logo.id } : {}) })
      .then((state) => {
        setSource(state.source);
        storedState.current = JSON.stringify(state);
        setTextDe(state.value?.text_de ?? "");
        setHtmlDe(state.value?.html_de ?? "");
        setLogo(state.logo ? { ...state.logo, preview_url: withTenantContext(state.logo.preview_url) } : null);
        // Show what is stored, not what was typed: the server is the authority on both fields.
        setRevision((n) => n + 1);
        edited.current = false;
        setChangedElsewhere(false);
        pushToast({ tone: "success", msg: t("settings.signatureSaved") });
      })
      .catch((e) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message || t("common.error") }))
      .finally(() => setBusy(false));
  };

  const sourceLabel =
    source === "tenant"
      ? t("settings.signatureSourceTenant")
      : source === "default"
        ? t("settings.signatureSourceDefault")
        : t("settings.signatureSourceNone");

  return (
    <Card className={`management-panel ${signatureStyles.settingsPanel}`}>
      <PanelLabel icon="mail" action={<StatusPill tone={source === "tenant" ? "success" : "neutral"}>{sourceLabel}</StatusPill>}>
        {t("settings.signature")}
      </PanelLabel>
      <p className={signatureStyles.intro}>{t("settings.signatureHint")}</p>
      {error !== null && (
        <Banner tone="danger" title={t("common.error")}>{error || t("common.error")}</Banner>
      )}
      {changedElsewhere && (
        <Banner tone="warning" title={t("settings.signatureChangedElsewhereTitle")}>
          {t("settings.signatureChangedElsewhereBody")}
        </Banner>
      )}
      {loading && <p className="subtle">{t("common.loading")}</p>}
      {!loading && (
        <div className={signatureStyles.settingsBody}>
          <SignatureEditor
            key={revision}
            initialHtml={htmlDe}
            initialText={textDe}
            onChange={onEditorChange}
            logo={logo}
            onLogoChange={(value) => { edited.current = true; setLogo(value); }}
            uploadLogo={async (file) => {
              const value = await cockpit.uploadSignatureLogo(file);
              return { ...value, preview_url: withTenantContext(value.preview_url) };
            }}
            onBusyChange={(value) => { if (value) edited.current = true; setLogoBusy(value); }}
            onValidityChange={setContentValid}
          />
          <div className={signatureStyles.footer}>
            <p className={signatureStyles.hint}>{t("settings.signatureEmptyHelp")}</p>
            <Button onClick={save} loading={busy} disabled={busy || logoBusy || !contentValid}>{t("settings.signatureSave")}</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function SystemCard({
  config,
  pushToast,
  onConfigChanged,
  canToggleMasterSend,
}: {
  config: AppConfigInfo | null;
  pushToast: SettingsScreenProps["pushToast"];
  onConfigChanged: SettingsScreenProps["onConfigChanged"];
  canToggleMasterSend: boolean;
}) {
  const t = useT();
  const [savingSend, setSavingSend] = React.useState(false);
  const runtime = config?.rule_parameters;
  const runtimeSource = config?.rules_authority && runtime?.source
    ? `${config.rules_authority} / ${runtime.source}`
    : t("common.notSet");

  const setFollowupSend = (enabled: boolean) => {
    setSavingSend(true);
    cockpit
      .setFollowupSendEnabled(enabled)
      .then(onConfigChanged)
      .then(() =>
        pushToast({
          tone: "success",
          title: enabled ? t("settings.followupEnabled") : t("settings.followupDisabled"),
        }),
      )
      .catch((e: any) =>
        pushToast({ tone: "danger", title: t("common.error"), msg: e?.message || t("settings.followupSaveFailed") }),
      )
      .finally(() => setSavingSend(false));
  };

  return (
    <>
      <Card className="settings-system-policy" aria-labelledby="settings-policy-title">
        <h3 id="settings-policy-title" className="settings-feature-title">{t("settings.businessPolicy")}</h3>
        <BusinessPolicyStatus config={config} context="settings" showTechnicalDetails={false} />
      </Card>
      <Card className="settings-system-features" aria-labelledby="settings-features-title">
        <div className="settings-feature-group">
          <h3 id="settings-features-title" className="settings-feature-title">{t("settings.featureFlags")}</h3>
          <p className="settings-feature-hint management-copy">{t("settings.flagsHint")}</p>
          <div className="system-feature-list" data-testid="system-feature-list">
            {FLAGS.map(({ key, failClosed }) => {
              const on = config?.flags?.[key];
              if (key === "followup_send_enabled") {
                return (
                  <FollowupSendFlagRow
                    key={key}
                    config={config}
                    saving={savingSend}
                    onChange={setFollowupSend}
                    canToggle={canToggleMasterSend}
                  />
                );
              }
              return (
                <FeatureFlagRow
                  key={key}
                  featureKey={key}
                  name={t(`flag.${key}`)}
                  enabled={on}
                  failClosed={failClosed}
                />
              );
            })}
          </div>
        </div>
        <details className="system-source-details" data-testid="system-source-details">
          <summary>{t("settings.sourceDetails")}</summary>
          <div className="system-source-details__group">
            <h3>{t("settings.businessPolicy")}</h3>
            <dl>
              <SystemSourceItem label={t("policy.codeRuleset")} value={config?.ruleset_version ?? t("common.notSet")} />
              <SystemSourceItem label={t("policy.runtimeSource")} value={runtimeSource} mono />
              <SystemSourceItem
                label={t("policy.effectiveHash")}
                value={runtime?.effective_rules_hash ?? t("common.notSet")}
                mono
              />
              <SystemSourceItem
                label={t("policy.diagnosticCode")}
                value={runtime?.error_code ?? t("common.notSet")}
                mono
              />
            </dl>
          </div>
          <div className="system-source-details__group">
            <h3>{t("settings.featureFlags")}</h3>
            <dl>
              {FLAGS.map(({ key }) => {
                const envEnabled = config?.flags?.followup_send_env_enabled ?? config?.flags?.followup_send_enabled;
                const authority = key === "followup_send_enabled"
                  ? t("settings.followupAuthority")
                  : t("settings.featureDeployAuthority");
                return (
                  <SystemSourceItem
                    key={key}
                    sourceKey={key}
                    label={t(`flag.${key}`)}
                    prefix={t("settings.featureAuthority")}
                    value={authority}
                    note={key === "followup_send_enabled" && envEnabled === false
                      ? t("settings.followupEnvLocked")
                      : undefined}
                  />
                );
              })}
            </dl>
          </div>
        </details>
      </Card>
    </>
  );
}

function SystemSourceItem({
  label,
  value,
  note,
  prefix,
  sourceKey,
  mono = false,
}: {
  label: string;
  value: string | number;
  note?: string;
  prefix?: string;
  sourceKey?: keyof AppConfigInfo["flags"];
  mono?: boolean;
}) {
  return (
    <div data-source-key={sourceKey}>
      <dt>{label}</dt>
      <dd className={mono ? "neo-mono" : undefined}>
        {prefix && <span className="system-source-details__prefix">{prefix}: </span>}
        <span className="system-source-details__value">{value}</span>
        {note && <span className="system-feature-fact__note"> · {note}</span>}
      </dd>
    </div>
  );
}

function FeatureStatus({ enabled, failClosed = false }: { enabled: boolean | undefined; failClosed?: boolean }) {
  const t = useT();
  if (enabled === undefined) return <span className="subtle">{t("common.notSet")}</span>;
  return (
    <StatusPill
      size="sm"
      variant="soft"
      tone={enabled ? "success" : (failClosed ? "warning" : "neutral")}
      label={enabled ? t("shell.active") : t("shell.off")}
    />
  );
}

function FeatureFlagRow({
  featureKey,
  name,
  enabled,
  failClosed,
}: {
  featureKey: keyof AppConfigInfo["flags"];
  name: string;
  enabled: boolean | undefined;
  failClosed?: boolean;
}) {
  const t = useT();
  return (
    <section className="system-feature-row" data-feature-key={featureKey}>
      <h4 className="system-feature-row__name">{name}</h4>
      <dl className="system-feature-row__facts">
        <div className="system-feature-fact">
          <dt>{t("settings.featureDeployStatus")}</dt>
          <dd><FeatureStatus enabled={enabled} failClosed={failClosed} /></dd>
        </div>
      </dl>
    </section>
  );
}

function FollowupSendFlagRow({
  config,
  saving,
  onChange,
  canToggle,
}: {
  config: AppConfigInfo | null;
  saving: boolean;
  onChange: (enabled: boolean) => void;
  canToggle: boolean;
}) {
  const t = useT();
  const flags = config?.flags;
  const envEnabled = flags?.followup_send_env_enabled ?? flags?.followup_send_enabled;
  const adminEnabled = flags?.followup_send_admin_enabled ?? flags?.followup_send_enabled;
  const effective = flags?.followup_send_enabled;
  const disabled = !config || saving || envEnabled === false;

  return (
    <section className="system-feature-row system-feature-row--controlled" data-feature-key="followup_send_enabled">
      <h4 className="system-feature-row__name">{t("flag.followup_send_enabled")}</h4>
      <dl className="system-feature-row__facts">
        <div className="system-feature-fact">
          <dt>{t("settings.featureEffectiveStatus")}</dt>
          <dd><FeatureStatus enabled={effective} failClosed /></dd>
        </div>
        {canToggle && (
          <div className="system-feature-fact system-feature-fact--control">
            <dt>{t("settings.followupAdminToggle")}</dt>
            <dd>
              <Switch
                checked={Boolean(adminEnabled)}
                disabled={disabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.currentTarget.checked)}
                aria-label={t("settings.followupAdminToggleAria")}
                onLabel={t("shell.active")}
                offLabel={t("shell.off")}
              />
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}
